import { useMutation, useQueryClient } from '@tanstack/react-query';
import GraphqlService from '../service/graphqlService';
import { useSnackbar } from '../providers/SnackbarContext';
import { useAuditLog, AUDIT_ENTITY_DEVICE_DEFENDER, AUDIT_CHANGE_DEVICE_BLOCKED } from './useAuditLog';

/**
 * Hook for blocking a device in Device Defender quarantine
 * Includes audit logging on successful block
 * 
 * @param {Object} options - Hook options
 * @param {Function} [options.onSuccess] - Callback on successful block
 * @param {Function} [options.onError] - Callback on error
 * @returns {Object} Mutation result with blockDevice function
 */
const useBlockDevice = ({ onSuccess, onError } = {}) => {
  const queryClient = useQueryClient();
  const openSnackBar = useSnackbar();
  const { logAuditChange } = useAuditLog({ silent: true });

  const mutation = useMutation(
    ({ projectId, deviceSerial }) => GraphqlService.blockDevice({ projectId, deviceSerial }),
    {
      onSuccess: (data, variables) => {
        // Invalidate quarantine status cache to trigger refetch
        queryClient.invalidateQueries(['deviceQuarantineStatus', variables.projectId, variables.deviceSerial]);
        
        // Log audit change
        logAuditChange(
          AUDIT_ENTITY_DEVICE_DEFENDER,
          AUDIT_CHANGE_DEVICE_BLOCKED,
          variables.deviceSerial,
          `Device blocked in project ${variables.projectId}`,
          variables.deviceSerial
        );

        openSnackBar('Device blocked successfully', 'success');
        
        if (onSuccess) {
          onSuccess(data, variables);
        }
      },
      onError: (error, variables) => {
        console.error('[useBlockDevice] Error:', error);
        openSnackBar(`Failed to block device: ${error.message}`, 'error');
        
        if (onError) {
          onError(error, variables);
        }
      },
    }
  );

  return {
    blockDevice: mutation.mutate,
    blockDeviceAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    reset: mutation.reset,
  };
};

export default useBlockDevice;
